/**
 * PlatformLeadsPage — CRM del super utente per l'acquisizione clienti di
 * YumaOS (rotta /aziende). Lista `platform_leads` con filtri client-side,
 * creazione manuale con dedupe P.IVA e drawer di dettaglio con modifica
 * diretta + risposte questionari.
 *
 * Doc canonico: docs/01-ARCHITECTURE/PLATFORM-CRM-QUESTIONARI-MODEL.md
 * Estetica: coerente con TenantsListPage (console editoriale telemetrica).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton,
  InputAdornment, MenuItem, Paper, Snackbar, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Refresh, Search } from '@mui/icons-material';
import { tokens } from '../theme';
import { SoftChip } from '../components/SoftChip';
import { NewLeadDialog } from '../components/platform-crm/NewLeadDialog';
import { LeadDetailDrawer } from '../components/platform-crm/LeadDetailDrawer';
import {
  FONTE_LABELS, FONTE_ORDER, STATO_LABELS, STATO_ORDER, STATO_TONES, formatDate,
} from '../components/platform-crm/labels';
import {
  distinctPartnerSegnalatori, listPlatformLeads, listQuestionnaires,
} from '../services/platformCrmService';
import type {
  PlatformLead, PlatformLeadFonte, PlatformLeadStato, PlatformQuestionnaire,
} from '../types/platformCrm';

export const PlatformLeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<PlatformLead[]>([]);
  const [questionnaires, setQuestionnaires] = useState<Map<string, PlatformQuestionnaire>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStato, setFilterStato] = useState<PlatformLeadStato | 'all'>('all');
  const [filterFonte, setFilterFonte] = useState<PlatformLeadFonte | 'all'>('all');
  /** Filtro partner segnalatore: 'all' oppure uno dei valori presenti nei dati. */
  const [filterPartner, setFilterPartner] = useState<string>('all');
  const [filterFounding, setFilterFounding] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // `?lead=<id>` apre subito la scheda di quell'iscritto (ci porta, per esempio, la conversazione con Yuma sul sito).
  const [parametri] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(() => parametri.get('lead'));
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [leadsList, questionnairesList] = await Promise.all([
        listPlatformLeads(),
        listQuestionnaires(),
      ]);
      setLeads(leadsList);
      setQuestionnaires(new Map(questionnairesList.map((q) => [q.id, q])));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento aziende');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Valori distinti di partner segnalatore presenti nei dati (niente hardcode)
  const partnerOptions = useMemo(() => distinctPartnerSegnalatori(leads), [leads]);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (filterStato !== 'all' && l.stato !== filterStato) return false;
      if (filterFonte !== 'all' && l.fonte !== filterFonte) return false;
      if (filterFounding && l.founding_partner !== true) return false;
      if (filterPartner !== 'all' && (l.partner_segnalatore ?? '').trim() !== filterPartner) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const haystack = [
          l.ragione_sociale, l.partita_iva, l.email, l.citta, l.provincia,
          l.settore, l.partner_segnalatore, l.referente_nome,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [leads, search, filterStato, filterFonte, filterFounding, filterPartner]);

  // Aggregati header
  const aggregates = useMemo(() => {
    let convertiti = 0;
    let persi = 0;
    for (const l of leads) {
      if (l.stato === 'convertito') convertiti += 1;
      else if (l.stato === 'perso') persi += 1;
    }
    return {
      total: leads.length,
      inFunnel: leads.length - convertiti - persi,
      convertiti,
    };
  }, [leads]);

  const selectedLead = useMemo(
    () => leads.find((l) => l.id === selectedId) ?? null,
    [leads, selectedId],
  );

  const showSnack = (msg: string, severity: 'success' | 'error') => setSnack({ msg, severity });

  return (
    <Box>
      {/* HEADER */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: tokens.font.display,
              fontWeight: 600,
              fontSize: { xs: '1.6rem', md: '1.95rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: tokens.color.graphite,
              fontVariationSettings: '"WONK" 0, "SOFT" 30',
            }}
          >
            Aziende
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, maxWidth: 540 }}>
            Funnel di acquisizione clienti di YumaOS: censimento, stato e risposte
            ai questionari. La conversione collega l'azienda al tenant, senza cancellare la storia.
          </Typography>
        </Box>

        <Stack direction="row" spacing={3} useFlexGap alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1.5 }}>
          <HeaderMetric label="Totale" value={aggregates.total.toString()} />
          <HeaderMetric
            label="In funnel"
            value={aggregates.inFunnel.toString()}
            accentColor={tokens.color.tierPro.main}
          />
          <HeaderMetric
            label="Convertiti"
            value={aggregates.convertiti.toString()}
            accentColor={tokens.color.success}
          />
          <Tooltip title="Ricarica">
            <IconButton onClick={load} disabled={loading} size="small">
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateOpen(true)}
          >
            Nuova azienda
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* FILTRI */}
      <Paper
        variant="outlined"
        sx={{ p: 1.5, mb: 2, borderColor: tokens.color.line, bgcolor: tokens.color.paper }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            placeholder="Cerca per nome, P.IVA, email, città o settore"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: tokens.color.mist }} />
                </InputAdornment>
              ),
              sx: { fontSize: '0.85rem' },
            }}
          />
          <TextField
            select size="small" value={filterStato}
            onChange={(e) => setFilterStato(e.target.value as PlatformLeadStato | 'all')}
            sx={{ minWidth: 210, '& .MuiSelect-select': { fontSize: '0.85rem' } }}
          >
            <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>Tutti gli stati</MenuItem>
            {STATO_ORDER.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: '0.85rem' }}>{STATO_LABELS[s]}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" value={filterFonte}
            onChange={(e) => setFilterFonte(e.target.value as PlatformLeadFonte | 'all')}
            sx={{ minWidth: 210, '& .MuiSelect-select': { fontSize: '0.85rem' } }}
          >
            <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>Tutte le fonti</MenuItem>
            {FONTE_ORDER.map((f) => (
              <MenuItem key={f} value={f} sx={{ fontSize: '0.85rem' }}>{FONTE_LABELS[f]}</MenuItem>
            ))}
          </TextField>
          {(partnerOptions.length > 0 || filterPartner !== 'all') && (
            <TextField
              select size="small" value={filterPartner}
              onChange={(e) => setFilterPartner(e.target.value)}
              sx={{ minWidth: 210, '& .MuiSelect-select': { fontSize: '0.85rem' } }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>Tutti i partner segnalatori</MenuItem>
              {/* Valori DISTINTI presenti nei dati; il valore filtrato resta
                  selezionabile anche se sparito dopo un ricaricamento */}
              {(partnerOptions.includes(filterPartner) || filterPartner === 'all'
                ? partnerOptions
                : [...partnerOptions, filterPartner]
              ).map((p) => (
                <MenuItem key={p} value={p} sx={{ fontSize: '0.85rem' }}>{p}</MenuItem>
              ))}
            </TextField>
          )}
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={filterFounding}
                onChange={(e) => setFilterFounding(e.target.checked)}
              />
            )}
            label={(
              <Typography sx={{ fontSize: '0.85rem', color: tokens.color.ash }}>
                Solo founding partner
              </Typography>
            )}
            sx={{ flexShrink: 0, mr: 0.5, ml: 0 }}
          />
        </Stack>
      </Paper>

      {/* TABELLA */}
      <Paper
        variant="outlined"
        sx={{ borderColor: tokens.color.line, bgcolor: tokens.color.paper, overflow: 'hidden' }}
      >
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
        ) : (
          <Table size="small" sx={{
            '& td': { py: 1.5 },
            '& tr:last-child td': { borderBottom: 0 },
            '& tbody tr': { transition: 'background-color 120ms ease', cursor: 'pointer' },
            '& tbody tr:hover': { backgroundColor: tokens.color.bone },
          }}>
            <TableHead>
              <TableRow>
                <TableCell>Ragione sociale</TableCell>
                <TableCell>P.IVA</TableCell>
                <TableCell>Fonte</TableCell>
                <TableCell>Stato funnel</TableCell>
                <TableCell>Città</TableCell>
                <TableCell align="right" sx={{ pr: 2 }}>Aggiornato</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((l) => (
                <TableRow key={l.id} onClick={() => setSelectedId(l.id)} hover>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite, lineHeight: 1.3 }}>
                        {l.ragione_sociale}
                      </Typography>
                      {l.founding_partner === true && <SoftChip label="Founding" tone="gold" />}
                    </Stack>
                    {l.settore && (
                      <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                        {l.settore}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box component="span" sx={{
                      fontFamily: tokens.font.mono,
                      fontSize: '0.78rem',
                      color: tokens.color.ash,
                      letterSpacing: '0.02em',
                    }}>
                      {l.partita_iva || '—'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <SoftChip label={FONTE_LABELS[l.fonte] ?? l.fonte} tone="info" />
                  </TableCell>
                  <TableCell>
                    <SoftChip
                      label={STATO_LABELS[l.stato] ?? l.stato}
                      tone={STATO_TONES[l.stato] ?? 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: tokens.color.ash }}>
                      {l.citta ? `${l.citta}${l.provincia ? ` (${l.provincia})` : ''}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 2 }}>
                    <Box component="span" sx={{
                      fontFamily: tokens.font.mono,
                      fontSize: '0.75rem',
                      color: tokens.color.mist,
                    }}>
                      {formatDate(l.updated_at ?? l.created_at)}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography
                      sx={{
                        fontFamily: tokens.font.display,
                        fontSize: '1.05rem',
                        fontWeight: 500,
                        fontStyle: 'italic',
                        color: tokens.color.mist,
                      }}
                    >
                      {leads.length === 0
                        ? 'Nessuna azienda censita: crea la prima con "Nuova azienda".'
                        : 'Nessuna azienda corrisponde ai filtri.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Dialog creazione */}
      <NewLeadDialog
        open={createOpen}
        partnerOptions={partnerOptions}
        onClose={() => setCreateOpen(false)}
        onCreated={async (id) => {
          setCreateOpen(false);
          showSnack('Azienda creata', 'success');
          await load();
          setSelectedId(id);
        }}
        onOpenExisting={(lead) => {
          // Il record potrebbe non essere nella lista caricata: lo inseriamo se manca
          setLeads((prev) => (prev.some((l) => l.id === lead.id) ? prev : [lead, ...prev]));
          setSelectedId(lead.id);
        }}
      />

      {/* Drawer dettaglio */}
      <LeadDetailDrawer
        lead={selectedLead}
        questionnaires={questionnaires}
        partnerOptions={partnerOptions}
        onClose={() => setSelectedId(null)}
        onSaved={load}
        showSnack={showSnack}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
};

// ============================================================================
// HEADER METRIC — pill compatta coerente con TenantsListPage
// ============================================================================

const HeaderMetric: React.FC<{ label: string; value: string; accentColor?: string }> = ({
  label, value, accentColor,
}) => (
  <Box sx={{ borderLeft: `2px solid ${accentColor ?? tokens.color.line}`, pl: 1.5 }}>
    <Typography
      sx={{
        fontFamily: tokens.font.body,
        fontSize: '0.62rem',
        fontWeight: 600,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: tokens.color.mist,
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: tokens.font.display,
        fontSize: '1.15rem',
        fontWeight: 600,
        color: accentColor ?? tokens.color.graphite,
        lineHeight: 1.2,
        mt: 0.25,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
  </Box>
);
